/*
 * myPokemonRecommendations.ts — honest, data-backed claims about ONE caught
 * Pokémon, for the My Pokémon detail page's recommendation block.
 *
 * Every recommendation traces to a checked-in ranking the app already ships:
 *
 *   - PvP: the PvPoke league rankings (battleMeta.generated.ts) — "Top #3
 *     Great League pick", including "when evolved to X" via the Game Master
 *     evolution chain (speciesBaseStats.generated.ts), and "with Dragon
 *     Breath + Sky Attack" when the ranked moveset differs from the ones on
 *     this Pokémon.
 *   - Raids: the PokéMiners-derived attacker rankings (raidMeta.ts) — "#7
 *     water raid attacker", with the best fast + charged moves.
 *   - IVs: this exact Pokémon's stat-product standing under the league caps
 *     (pvpRank) — species viability says whether the league matters; the IVs
 *     say whether THIS one is the copy to build.
 *
 * NOTHING is invented: a species with no claim gets no filler. The block's
 * empty state is the page's job.
 */
import { BATTLE_META, type BattleMetaEntry } from "./generated/battleMeta.generated";
import {
  evolutionChainFor,
} from "./generated/speciesBaseStats.generated";
import { NO_RAID_FILTERS, rankRaidAttackers } from "./raidMeta";
import { myPokemonTypes } from "./myPokemonTypes";
import { speciesName } from "../data/speciesNames";
import type { MyPokemonRecord } from "./myPokemonStore";
import { deriveMyPokemon } from "./myPokemonDerived";

export interface MyPokemonRecommendation {
  kind: "pvp" | "pvp-evolved" | "raid" | "iv" | "trade" | "transfer" | "showcase" | "better-copy";
  /** The one-line claim, e.g. "Top #3 Great League pick when evolved to Garchomp". */
  headline: string;
  /** Supporting line — the moveset to run, the score, the caveat. */
  detail: string | null;
  /** For trade recommendations: the friend to open, so the UI can deep-link. */
  friendId?: number;
  /** For better-copy calls: the record that beats this one, so the UI can open it. */
  betterRecordId?: string;
}

/** How deep into a league's top-100 a claim is worth making. */
const PVP_CLAIM_RANK = 40;
/** How deep into a type's attacker ranking a raid claim is worth making. */
const RAID_CLAIM_RANK = 15;

function bestLeagueEntry(dexId: number): { league: string; entry: BattleMetaEntry } | null {
  let best: { league: string; entry: BattleMetaEntry } | null = null;
  for (const league of BATTLE_META) {
    // The non-shadow row: this scanner cannot see shadow yet, so shadow rows
    // would claim a bonus the Pokémon may not have.
    const entry = league.entries.find((e) => e.dexId === dexId && !e.shadow);
    if (!entry || entry.rank > PVP_CLAIM_RANK) continue;
    if (!best || entry.rank < best.entry.rank) best = { league: league.label, entry };
  }
  return best;
}

function normalizeMove(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** "with Dragon Breath + Sky Attack" when the ranked moves differ from ours. */
function movesetDetail(entry: BattleMetaEntry, record: MyPokemonRecord): string | null {
  if (entry.moves.length === 0) return null;
  const have = new Set(record.moves.map((m) => normalizeMove(m.name)));
  const missing = entry.moves.filter((m) => !have.has(normalizeMove(m)));
  if (record.moves.length > 0 && missing.length === 0) {
    return `Already running the ranked moveset (${entry.moves.join(" + ")}).`;
  }
  return `Best with ${entry.moves.join(" + ")}.`;
}

export interface PvpStanding {
  /** 1-based rank inside the league's top-40 claim window. */
  rank: number;
  /** League label as BATTLE_META names it, e.g. "Great League". */
  league: string;
  /** Species to evolve into when the standing needs an evolution, else null. */
  evolveInto: string | null;
}

/**
 * The single strongest PvP claim for this species, evolution-aware: the
 * compact form of recommendation kinds 1+2, for the live-scan PiP hero line
 * (2026-08-26 ask - a Growlithe that is nothing in PvP today but ranks #1 as
 * Arcanine should say so while it is being scanned).
 */
export function bestPvpStanding(dexId: number): PvpStanding | null {
  const self = bestLeagueEntry(dexId);
  let bestEvolved: { league: string; entry: BattleMetaEntry; into: number } | null = null;
  for (const evolved of evolutionChainFor(dexId)) {
    const hit = bestLeagueEntry(evolved);
    if (!hit) continue;
    if (!bestEvolved || hit.entry.rank < bestEvolved.entry.rank) {
      bestEvolved = { ...hit, into: evolved };
    }
  }
  if (bestEvolved && (!self || bestEvolved.entry.rank < self.entry.rank)) {
    return {
      rank: bestEvolved.entry.rank,
      league: bestEvolved.league,
      evolveInto: speciesName(bestEvolved.into),
    };
  }
  if (self) return { rank: self.entry.rank, league: self.league, evolveInto: null };
  return null;
}

export function recommendationsFor(
  record: MyPokemonRecord,
  derived?: { greatPct: number | null; ultraPct: number | null },
): MyPokemonRecommendation[] {
  const out: MyPokemonRecommendation[] = [];
  const dexId = record.speciesId;
  if (dexId == null) return out;

  // 1) PvP, as it stands today.
  const self = bestLeagueEntry(dexId);
  if (self) {
    out.push({
      kind: "pvp",
      headline: `Top #${self.entry.rank} ${self.league} pick (score ${self.entry.score}).`,
      detail: movesetDetail(self.entry, record),
    });
  }

  // 2) PvP after evolving — the strongest claim across the whole chain,
  //    only when it beats (or the species lacks) its own standing.
  let bestEvolved: { league: string; entry: BattleMetaEntry; into: number } | null = null;
  for (const evolved of evolutionChainFor(dexId)) {
    const hit = bestLeagueEntry(evolved);
    if (!hit) continue;
    if (!bestEvolved || hit.entry.rank < bestEvolved.entry.rank) {
      bestEvolved = { ...hit, into: evolved };
    }
  }
  if (bestEvolved && (!self || bestEvolved.entry.rank < self.entry.rank)) {
    out.push({
      kind: "pvp-evolved",
      headline: `Top #${bestEvolved.entry.rank} ${bestEvolved.league} pick when evolved to ${speciesName(bestEvolved.into)}.`,
      detail:
        bestEvolved.entry.moves.length > 0
          ? `Best with ${bestEvolved.entry.moves.join(" + ")}.`
          : null,
    });
  }

  // 3) Raids: this species' standing among attackers OF ITS OWN TYPES,
  //    against a neutral boss — the "am I a poison attacker worth building?"
  //    question. Evolution-aware raid claims are a follow-up.
  const raidTypes = new Set<string>();
  for (const t of myPokemonTypes(record)) raidTypes.add(t);
  for (const t of raidTypes) {
    const { rows } = rankRaidAttackers(
      { ...NO_RAID_FILTERS, attackerType: t },
      "er",
      RAID_CLAIM_RANK,
    );
    const hit = rows.find((r) => r.attacker.dexId === dexId && !r.shadow);
    if (!hit) continue;
    const moves =
      hit.fast && hit.charged ? `${hit.fast.name} + ${hit.charged.name}` : null;
    const have = new Set(record.moves.map((m) => normalizeMove(m.name)));
    const needsSwap =
      moves != null &&
      record.moves.length > 0 &&
      ![hit.fast!.name, hit.charged!.name].every((m) => have.has(normalizeMove(m)));
    out.push({
      kind: "raid",
      headline: `#${hit.rank} ${t} raid attacker.`,
      detail: moves
        ? needsSwap
          ? `Change its moves to ${moves}.`
          : `Best with ${moves}.`
        : null,
    });
  }

  // 4) The IVs: are THESE the numbers worth investing in? Only said when the
  //    species carries a PvP claim, so the block never praises IVs on a
  //    Pokémon with nowhere to use them.
  if ((self || bestEvolved) && derived) {
    const gl = derived.greatPct;
    const ul = derived.ultraPct;
    const best = Math.max(gl ?? 0, ul ?? 0);
    if (best >= 98) {
      const league = (gl ?? 0) >= (ul ?? 0) ? "Great" : "Ultra";
      out.push({
        kind: "iv",
        headline: `These IVs are ${best}% of the perfect ${league} League spread.`,
        detail: "Worth the stardust if you build one for that league.",
      });
    }
  }

  return out;
}

// ── Trade or transfer ─────────────────────────────────────────────────────────

/** One friend from GET /api/friends/who-has/:speciesId. `hasLucky` is the
 * 2026-08-20 additive field — absent until the worker deploy reaches prod. */
export interface WhoHasFriend {
  accountId: number;
  name: string;
  hasIt: boolean;
  hasSpecies: boolean;
  hasLucky?: boolean;
}

export interface WhoHasAnswer {
  tradable: boolean;
  friends: WhoHasFriend[];
}

/** The slice of a live event the candy matcher needs. */
export interface CandyEventLite {
  name: string;
  start: string;
  bonuses?: string[];
}

/**
 * The earliest event whose stated bonuses (or, failing that, name) promise a
 * candy multiplier — the "transfer it THEN" window. Pass active first, then
 * upcoming, in feed order; the first hit wins.
 */
export function findNextCandyEvent(
  events: readonly CandyEventLite[],
): CandyEventLite & { bonus: string | null } | null {
  const multiplied = (s: string) =>
    /(\d+\s*[×x]|double|triple)/i.test(s) && /candy/i.test(s);
  for (const e of events) {
    const bonus = (e.bonuses ?? []).find(multiplied) ?? null;
    if (bonus) return { ...e, bonus };
    if (multiplied(e.name)) return { ...e, bonus: null };
  }
  return null;
}

export interface TradeOrTransferInputs {
  record: MyPokemonRecord;
  /** How many meta claims recommendationsFor produced (0 = nothing to keep it for). */
  metaClaims: number;
  derived: { greatPct: number | null; ultraPct: number | null } | null;
  whoHas: WhoHasAnswer | null;
  /** Active-then-upcoming live events, or null while loading. */
  events: readonly CandyEventLite[] | null;
}

/** Below this best-league percent the IVs are not an argument for keeping it. */
const KEEP_IV_PCT = 95;

/**
 * The "so what do I DO with it" call, made only when nothing above argued for
 * keeping it: no meta claim, IVs known and unremarkable. Ladder:
 *
 *   1. A friend lacks the SPECIES → the strongest trade: they need the dex entry.
 *   2. A friend lacks a LUCKY of it → "add it to their trades" (any copy traded
 *      to them rolls the lucky chance — an old one especially).
 *   3. Everyone has it (or GO refuses to trade the species) → transfer for
 *      candy, timed to the next stated candy-bonus event when the feed has one.
 *
 * Null while a decision would be premature: meta claims exist, IVs unread,
 * IVs are keeper-grade, the species is unresolved, or friend data is loading.
 */
export function tradeOrTransferRecommendation(
  i: TradeOrTransferInputs,
): MyPokemonRecommendation | null {
  const { record, metaClaims, derived, whoHas } = i;
  if (record.speciesId == null || metaClaims > 0) return null;
  if (record.ivAtk == null || record.ivDef == null || record.ivHp == null) return null;
  const bestPct = Math.max(derived?.greatPct ?? 0, derived?.ultraPct ?? 0);
  if (bestPct >= KEEP_IV_PCT) return null;
  if (!whoHas) return null;

  const species = speciesName(record.speciesId);

  if (whoHas.tradable) {
    const needsSpecies = whoHas.friends.filter((f) => !f.hasSpecies);
    if (needsSpecies.length > 0) {
      const [first, ...rest] = needsSpecies;
      return {
        kind: "trade",
        headline: `Add it to your trades: ${first.name} doesn't have ${species} at all.`,
        detail:
          rest.length > 0
            ? `${rest.length} other friend${rest.length === 1 ? "" : "s"} need${rest.length === 1 ? "s" : ""} it too.`
            : "Open their trades and add it.",
        friendId: first.accountId,
      };
    }
    const needsLucky = whoHas.friends.filter((f) => f.hasLucky === false);
    if (needsLucky.length > 0) {
      const [first, ...rest] = needsLucky;
      return {
        kind: "trade",
        headline: `Add it to ${first.name}'s trades. They still need a lucky ${species}.`,
        detail:
          rest.length > 0
            ? `${rest.length} other friend${rest.length === 1 ? "" : "s"} still need${rest.length === 1 ? "s" : ""} a lucky one too.`
            : "Any copy you trade them rolls the lucky chance, an old one especially.",
        friendId: first.accountId,
      };
    }
  }

  // Nobody needs it (or GO won't trade the species): candy it is.
  const candy = i.events ? findNextCandyEvent(i.events) : null;
  const everyoneHasIt = whoHas.friends.length > 0;
  const headline = whoHas.tradable
    ? everyoneHasIt
      ? `All your friends have ${species}. Safe to transfer for candy.`
      : `Safe to transfer ${species} for candy.`
    : `${species} can't be traded. Safe to transfer for candy.`;
  let detail: string | null = null;
  if (candy) {
    const when = new Date(candy.start);
    const label = Number.isNaN(when.getTime())
      ? candy.start
      : when.toLocaleDateString(undefined, {
          weekday: "short",
          day: "numeric",
          month: "short",
        });
    detail = `Next candy bonus: ${candy.name}, ${label}${candy.bonus ? ` (${candy.bonus})` : ""}.`;
  }
  return { kind: "transfer", headline, detail };
}

// ── A better copy already in the bag ──────────────────────────────────────────

/** How much better the other copy's IV total must be before it is "better". */
const BETTER_COPY_IV_MARGIN = 5;

function bestLeaguePctOf(d: { greatPct: number | null; ultraPct: number | null }): number {
  return Math.max(d.greatPct ?? 0, d.ultraPct ?? 0);
}

/**
 * "Your best one is that one" (2026-10-10 owner ask, made actionable
 * 2026-10-12): the transfer call a trainer can make from their OWN scans,
 * with no friend data needed. Fires when another scanned copy of the same
 * species AND form has an IV total clearly ahead (5 points or more) and is no
 * worse in its best league, so the copy in hand is strictly the lesser one.
 * The headline names the BEST such copy, and the page offers to open it and
 * to clean up the rest of the species in Box Cleanup. Says nothing when:
 *
 *   - this copy is special (shiny, lucky, shadow, purified, Mega, Best Buddy):
 *     those are kept for what they are, not for their numbers;
 *   - this copy's IVs are unread (nothing to compare), or keeper-grade for a
 *     league (the IV call above already argued for it);
 *   - the other copy is the same record, or lacks IVs.
 *
 * The best challenger wins: highest IV total, then highest best-league percent.
 */
export function betterCopyRecommendation(
  records: readonly MyPokemonRecord[],
  record: MyPokemonRecord,
  derived: { greatPct: number | null; ultraPct: number | null; ivPercent: number | null },
): MyPokemonRecommendation | null {
  if (record.speciesId == null) return null;
  if (record.ivAtk == null || record.ivDef == null || record.ivHp == null) return null;
  if (derived.ivPercent == null) return null;
  if (bestLeaguePctOf(derived) >= KEEP_IV_PCT) return null;
  if (
    record.isShiny || record.isLucky || record.isShadow || record.isPurified ||
    record.isMega || record.isBestBuddy
  ) {
    return null;
  }
  let best: { r: MyPokemonRecord; iv: number; league: number } | null = null;
  for (const other of records) {
    if (other.id === record.id || other.speciesId !== record.speciesId) continue;
    if ((other.formId ?? null) !== (record.formId ?? null)) continue;
    if (other.ivAtk == null || other.ivDef == null || other.ivHp == null) continue;
    const d = deriveMyPokemon(other);
    if (d.ivPercent == null) continue;
    if (d.ivPercent < derived.ivPercent + BETTER_COPY_IV_MARGIN) continue;
    const league = bestLeaguePctOf(d);
    if (league < bestLeaguePctOf(derived)) continue;
    if (!best || d.ivPercent > best.iv || (d.ivPercent === best.iv && league > best.league)) {
      best = { r: other, iv: d.ivPercent, league };
    }
  }
  if (!best) return null;
  const species = speciesName(record.speciesId);
  const otherName = best.r.nickname && best.r.nickname !== species ? best.r.nickname : null;
  const mine = Math.round(derived.ivPercent);
  const theirs = Math.round(best.iv);
  return {
    kind: "better-copy",
    headline: otherName
      ? `Your best ${species} is ${otherName} at ${theirs}% IV. This one is ${mine}%.`
      : `Your best ${species} is ${theirs}% IV. This one is ${mine}%.`,
    detail: "Keep the best, transfer the rest for candy. Box Cleanup lists every copy and the GO search to clear them.",
    betterRecordId: best.r.id,
  };
}
